import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import { toast } from 'sonner'
import { ArrowLeft, Pencil, Search, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Filter } from 'lucide-react'
import { formatDateTime } from '@/lib/dateUtils'

import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { PageHeader, StatCard, StatCardGroup } from '@/components/shared'
import { emailSegmentService } from '@/services/emailSegmentService'

const PAGE_SIZE = 20

const consentVariant = {
  SUBSCRIBED: 'default',
  PENDING: 'secondary',
  UNSUBSCRIBED: 'outline',
}

const campaignStatusVariant = {
  DRAFT: 'secondary',
  SCHEDULED: 'default',
  SENDING: 'default',
  SENT: 'secondary',
  CANCELLED: 'outline',
  FAILED: 'outline',
}

const OPERATOR_LABELS = {
  EQUALS: 'equals',
  NOT_EQUALS: 'does not equal',
  CONTAINS: 'contains',
  NOT_CONTAINS: 'does not contain',
  STARTS_WITH: 'starts with',
  ENDS_WITH: 'ends with',
  GREATER_THAN: 'greater than',
  LESS_THAN: 'less than',
  IS_EMPTY: 'is empty',
  IS_NOT_EMPTY: 'is not empty',
}

const SegmentContactsTab = ({ id }) => {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput)
      setPage(1)
    }, 300)
    return () => clearTimeout(timeout)
  }, [searchInput])

  const { data: contactsData, isLoading: contactsLoading } = useQuery(
    ['email-segment-contacts', id, page, search],
    () => emailSegmentService.getSegmentContacts(id, { page, limit: PAGE_SIZE, search: search || undefined }),
    { keepPreviousData: true }
  )

  const pagination = contactsData?.data?.pagination
  const contacts = contactsData?.data?.contacts || []

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Matching Contacts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search contacts..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="pl-9 bg-background"
          />
        </div>

        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="bg-muted/50 font-medium">Email</TableHead>
                <TableHead className="bg-muted/50 font-medium">Name</TableHead>
                <TableHead className="bg-muted/50 font-medium">Consent</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {contactsLoading ? (
                [...Array(3)].map((_, i) => (
                  <TableRow key={i}>
                    {[...Array(3)].map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}
                  </TableRow>
                ))
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="h-24 text-center text-muted-foreground">
                    {search ? 'No contacts match your search.' : 'No matching contacts.'}
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((c) => (
                  <TableRow key={c.id} className="border-border">
                    <TableCell>{c.email}</TableCell>
                    <TableCell>{[c.first_name, c.last_name].filter(Boolean).join(' ') || '—'}</TableCell>
                    <TableCell>
                      <Badge variant={consentVariant[c.consent_status] || 'secondary'}>{c.consent_status}</Badge>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {pagination && pagination.total > 0 && (
          <div className="flex items-center justify-between px-2">
            <span className="text-sm text-muted-foreground">
              {pagination.total} contact{pagination.total === 1 ? '' : 's'}
            </span>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage(1)}
                  disabled={pagination.page <= 1}
                >
                  <ChevronsLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={pagination.page <= 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setPage(pagination.totalPages)}
                  disabled={pagination.page >= pagination.totalPages}
                >
                  <ChevronsRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const SegmentAnalyticsTab = ({ id }) => {
  const navigate = useNavigate()
  const { data, isLoading } = useQuery(
    ['email-segment-analytics', id],
    () => emailSegmentService.getSegmentAnalytics(id),
    { onError: () => toast.error('Failed to fetch segment analytics') }
  )

  const summary = data?.data?.summary
  const campaigns = data?.data?.campaigns || []

  if (isLoading) return <Skeleton className="h-64 w-full" />

  return (
    <div className="space-y-6">
      {summary?.totalSent > 0 && (
        <StatCardGroup>
          <StatCard title="Total Sent" value={summary.totalSent} contentClassName="p-4" />
          <StatCard title="Open Rate" value={`${summary.openRate}%`} contentClassName="p-4" />
          <StatCard title="Click Rate" value={`${summary.clickRate}%`} contentClassName="p-4" />
          <StatCard title="Bounce Rate" value={`${summary.bounceRate}%`} contentClassName="p-4" />
        </StatCardGroup>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Email Performance by Campaign</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent border-border">
                <TableHead className="bg-muted/50 font-medium">Status</TableHead>
                <TableHead className="bg-muted/50 font-medium">Campaign</TableHead>
                <TableHead className="bg-muted/50 font-medium">Sent At</TableHead>
                <TableHead className="bg-muted/50 font-medium text-right">Sent</TableHead>
                <TableHead className="bg-muted/50 font-medium text-right">Open Rate</TableHead>
                <TableHead className="bg-muted/50 font-medium text-right">Click Rate</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {campaigns.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="h-24 text-center text-muted-foreground">
                    No campaigns have targeted this segment yet.
                  </TableCell>
                </TableRow>
              ) : (
                campaigns.map((c) => (
                  <TableRow
                    key={c.campaignId}
                    className="border-border cursor-pointer"
                    onClick={() => navigate(`/marketing-email/campaigns/${c.campaignId}`)}
                  >
                    <TableCell><Badge variant={campaignStatusVariant[c.campaignStatus] || 'secondary'}>{c.campaignStatus}</Badge></TableCell>
                    <TableCell className="font-medium">{c.campaignName}</TableCell>
                    <TableCell>{c.campaignSentAt ? formatDateTime(c.campaignSentAt) : '—'}</TableCell>
                    <TableCell className="text-right">{c.totalSent}</TableCell>
                    <TableCell className="text-right">{c.openRate}%</TableCell>
                    <TableCell className="text-right">{c.clickRate}%</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  )
}

const SegmentRulesTab = ({ segment }) => {
  const rules = segment.rules || []
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Rules</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {rules.length === 0 ? (
          <p className="p-4 text-sm text-muted-foreground">No conditions — matches all subscribed contacts.</p>
        ) : (
          <div className="divide-y divide-border">
            {rules.map((rule, idx) => (
              <div key={idx} className="flex flex-wrap items-center gap-3 px-4 py-3">
                {idx === 0 ? (
                  <span className="w-14 shrink-0 text-xs font-semibold uppercase tracking-wider text-primary">
                    Where
                  </span>
                ) : (
                  <span className="inline-flex h-6 w-14 shrink-0 items-center justify-center rounded bg-muted text-xs font-bold uppercase tracking-wider text-foreground">
                    {rule.join || 'AND'}
                  </span>
                )}
                <span className="rounded-md border bg-muted px-2 py-1 text-xs font-semibold text-foreground">
                  {rule.field?.replace('custom:', '')}
                </span>
                <span className="text-xs text-muted-foreground">
                  {OPERATOR_LABELS[rule.operator] || rule.operator}
                </span>
                {rule.value !== undefined && rule.value !== '' && rule.operator !== 'IS_EMPTY' && rule.operator !== 'IS_NOT_EMPTY' && (
                  <span className="rounded-md border bg-background px-2 py-1 text-xs font-medium text-foreground">
                    {rule.value}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

const SegmentDetail = () => {
  const { id } = useParams()
  const navigate = useNavigate()

  const { data, isLoading } = useQuery(['email-segment', id], () => emailSegmentService.getSegmentById(id), {
    onError: () => toast.error('Failed to fetch segment'),
  })
  const { data: analyticsSummaryData } = useQuery(['email-segment-analytics', id], () => emailSegmentService.getSegmentAnalytics(id))

  const segment = data?.data?.segment
  const summary = analyticsSummaryData?.data?.summary

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!segment) {
    return <div className="text-center py-12 text-muted-foreground">Segment not found.</div>
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={
          <span className="inline-flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-md bg-primary/10 text-primary shrink-0 translate-y-0.5">
              <Filter className="h-3.5 w-3.5" />
            </span>
            {segment.name}
          </span>
        }
        description={<span className="pl-8 block">{segment.description || 'No description'}</span>}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={() => navigate('/marketing-email/segments')}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            <Button size="sm" onClick={() => navigate(`/marketing-email/segments/${id}/edit`)}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit
            </Button>
          </>
        }
      />

      <StatCardGroup>
        <StatCard title="Est. Recipients" value={segment.last_evaluated_count ?? '—'} contentClassName="p-4" />
        <StatCard title="Campaigns Using This Segment" value={summary?.campaignsUsingSegment ?? 0} contentClassName="p-4" />
        <StatCard title="Campaigns Sent" value={summary?.campaignsSent ?? 0} contentClassName="p-4" />
      </StatCardGroup>

      <Tabs defaultValue="contacts">
        <TabsList>
          <TabsTrigger value="contacts">Contacts</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="rules">Rules</TabsTrigger>
        </TabsList>
        <TabsContent value="contacts" className="mt-4"><SegmentContactsTab id={id} /></TabsContent>
        <TabsContent value="analytics" className="mt-4"><SegmentAnalyticsTab id={id} /></TabsContent>
        <TabsContent value="rules" className="mt-4"><SegmentRulesTab segment={segment} /></TabsContent>
      </Tabs>
    </div>
  )
}

export default SegmentDetail
